/**
 * SCOUT'S WORDS, AS THE READER SHOULD SEE THEM — a small, safe Markdown
 * renderer for the Main Chat.
 *
 * WHY THIS EXISTS. The workspace rendered every message as a single text node
 * inside a `white-space: pre-wrap` block. That was the right first decision:
 * Preact escapes text, nothing sets `innerHTML`, and a message containing `<`
 * was prose. But Scout writes Markdown — headings, emphasis, lists, tables,
 * fenced code — and the CLI has rendered that language since it existed. The
 * Web surface printed the syntax instead: literal `**emphasis**`, literal
 * fences, tables collapsed into pipe characters. The Owner reported it, and
 * the fix is a renderer rather than a request that Scout stop writing the way
 * it writes.
 *
 * WHAT IT IS NOT. It is not an HTML parser and it never produces an HTML
 * string. It produces a TREE of typed nodes, and the Preact component below
 * maps that tree to elements. There is no `innerHTML`, no `dangerouslySet*
 * InnerHTML`, no DOM fragment built from text, and no HTML entity decoding: a
 * `<script>` in a message is the characters `<script>`, which is what the old
 * surface showed and what this one still shows. Raw HTML is deliberately not
 * part of the supported subset.
 *
 * LINKS ARE SANITIZED BY SCHEME, NOT BY ESCAPING. Only `http:`, `https:`,
 * `mailto:`, a same-origin path (`/…`, but not `//…`) and a fragment (`#…`)
 * survive. `javascript:`, `data:`, `vbscript:` and anything with a control
 * character or whitespace in it render as their label with no `href` at all.
 * External links carry `rel="noopener noreferrer nofollow"` and open in a new
 * tab; the app itself is never navigated by a link in a message.
 *
 * THE SUBSET IS THE CLI'S SUBSET, PLUS TABLES. The terminal renderer already
 * established the language Scout actually uses: ATX headings, bold, italic,
 * inline code, fenced code, ordered and unordered lists, quotes, horizontal
 * rules and paragraphs. Tables are added because a research answer uses them
 * and a table that arrives as pipes is worse than no table. Nested lists are
 * flattened rather than guessed at; a table is the one construct with a
 * horizontal-scroll wrapper so a wide row cannot widen the page on a phone.
 *
 * UNCLOSED SYNTAX STAYS LITERAL. A single `*` or an unclosed fence is not
 * repaired into emphasis or code — repairing would show a reader a different
 * claim than the one written. Valid Markdown (the case the Owner reported)
 * is consumed; text that merely contains a marker is left alone.
 */

import type { ComponentChildren } from 'preact';

export type MarkdownInline =
	| { kind: 'text'; text: string }
	| { kind: 'code'; text: string }
	| { kind: 'strong'; children: MarkdownInline[] }
	| { kind: 'em'; children: MarkdownInline[] }
	| { kind: 'link'; href: string; external: boolean; children: MarkdownInline[] }
	| { kind: 'break' };

export type MarkdownBlock =
	| { kind: 'paragraph'; children: MarkdownInline[] }
	| { kind: 'heading'; level: 2 | 3 | 4; children: MarkdownInline[] }
	| { kind: 'code'; language: string | null; text: string }
	| { kind: 'list'; ordered: boolean; start: number; items: MarkdownInline[][] }
	| { kind: 'quote'; blocks: MarkdownBlock[] }
	| { kind: 'rule' }
	| { kind: 'table'; header: MarkdownInline[][]; rows: MarkdownInline[][][] };

/** The largest message this parser will walk. The server bounds an answer far
 *  below this; the bound exists so a malformed row cannot make the browser do
 *  unbounded work. A message over the bound is rendered as plain text rather
 *  than cut, because cutting it would hide the rest of what was said. */
export const MARKDOWN_MAX_CHARS = 200_000;

/**
 * A LINK THIS RENDERER WILL MAKE CLICKABLE, OR NULL.
 *
 * The null is the security answer, not a formatting preference: an unsafe
 * scheme is not escaped into a harmless-looking URL, it is removed. Callers
 * render the label as text when this returns null.
 */
export function safeMarkdownHref(raw: string): { href: string; external: boolean } | null {
	const value = raw.trim();
	if (value.length === 0) return null;
	// Control characters and whitespace inside a URL are how a browser is
	// talked into reading a scheme it did not appear to have.
	if (/[\u0000- \u007f]/.test(value)) return null;
	const lower = value.toLowerCase();
	if (lower.startsWith('https://') || lower.startsWith('http://')) {
		return { href: value, external: true };
	}
	if (lower.startsWith('mailto:')) {
		return { href: value, external: true };
	}
	// A SAME-ORIGIN PATH. `//host` is protocol-relative egress and is refused.
	if (value.startsWith('/') && !value.startsWith('//')) {
		return { href: value, external: false };
	}
	if (value.startsWith('#')) return { href: value, external: false };
	return null;
}

function isWordChar(value: string): boolean {
	return /[A-Za-z0-9_]/.test(value);
}

/** The inline language: code, links, strong, emphasis, escapes and soft
 *  breaks. Every branch consumes the marker it recognized; a marker that has
 *  no valid closing partner falls through as literal text. */
export function parseMarkdownInline(text: string): MarkdownInline[] {
	const nodes: MarkdownInline[] = [];
	let buffer = '';
	let index = 0;
	const flush = (): void => {
		if (buffer.length > 0) {
			nodes.push({ kind: 'text', text: buffer });
			buffer = '';
		}
	};

	while (index < text.length) {
		const char = text[index] as string;

		// A BACKSLASH ESCAPES THE NEXT CHARACTER, as Markdown's own rule.
		if (char === '\\' && index + 1 < text.length) {
			buffer += text[index + 1];
			index += 2;
			continue;
		}

		if (char === '`') {
			const end = text.indexOf('`', index + 1);
			if (end > index + 1) {
				flush();
				nodes.push({ kind: 'code', text: text.slice(index + 1, end) });
				index = end + 1;
				continue;
			}
		}

		if (char === '*' && text[index + 1] === '*') {
			const end = text.indexOf('**', index + 2);
			if (end > index + 2) {
				flush();
				nodes.push({
					kind: 'strong',
					children: parseMarkdownInline(text.slice(index + 2, end)),
				});
				index = end + 2;
				continue;
			}
		}

		if (char === '*') {
			const end = text.indexOf('*', index + 1);
			if (
				end > index + 1 &&
				text[index + 1] !== ' ' &&
				text[end - 1] !== ' ' &&
				text[end + 1] !== '*'
			) {
				flush();
				nodes.push({ kind: 'em', children: parseMarkdownInline(text.slice(index + 1, end)) });
				index = end + 1;
				continue;
			}
		}

		// UNDERSCORE EMPHASIS ONLY AT WORD BOUNDARIES. Without this,
		// `scout_source_reads` would gain italics in the middle of an identifier
		// — the exact kind of presentation bug this renderer exists to remove.
		if (
			char === '_' &&
			(index === 0 || !isWordChar(text[index - 1] as string)) &&
			text[index + 1] !== ' ' &&
			text[index + 1] !== '_'
		) {
			let end = index + 1;
			while (end < text.length) {
				const found = text.indexOf('_', end);
				if (found === -1) break;
				if (
					text[found - 1] !== ' ' &&
					(found + 1 >= text.length || !isWordChar(text[found + 1] as string))
				) {
					end = found;
					break;
				}
				end = found + 1;
			}
			if (end > index + 1 && end < text.length) {
				flush();
				nodes.push({ kind: 'em', children: parseMarkdownInline(text.slice(index + 1, end)) });
				index = end + 1;
				continue;
			}
		}

		if (char === '[') {
			const labelEnd = text.indexOf('](', index + 1);
			if (labelEnd > index + 1) {
				const urlEnd = text.indexOf(')', labelEnd + 2);
				if (urlEnd > labelEnd + 2) {
					const label = text.slice(index + 1, labelEnd);
					const target = safeMarkdownHref(text.slice(labelEnd + 2, urlEnd));
					if (target !== null) {
						flush();
						nodes.push({
							kind: 'link',
							href: target.href,
							external: target.external,
							children: parseMarkdownInline(label),
						});
						index = urlEnd + 1;
						continue;
					}
					// AN UNSAFE LINK RENDERS AS ITS LABEL, not as a dead link and
					// not as the raw syntax. The URL is deliberately dropped.
					flush();
					nodes.push(...parseMarkdownInline(label));
					index = urlEnd + 1;
					continue;
				}
			}
		}

		if (char === '\n') {
			flush();
			nodes.push({ kind: 'break' });
			index += 1;
			continue;
		}

		buffer += char;
		index += 1;
	}

	flush();
	return nodes;
}

const FENCE = /^\s*(```|~~~)\s*([A-Za-z0-9_+.-]{0,20})\s*$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const RULE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
const LIST_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
// ONE OR MORE COLUMNS. The group is `*` rather than `+` so a single-column
// table — which is a real answer format for a list of claims — is still a
// table; the leading mandatory dash run is what keeps a bare `---` rule out.
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)*\|?\s*$/;

function splitTableRow(line: string): string[] {
	const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
	const cells: string[] = [];
	let current = '';
	let escaped = false;
	for (const char of trimmed) {
		if (escaped) {
			current += char;
			escaped = false;
			continue;
		}
		if (char === '\\') {
			escaped = true;
			continue;
		}
		if (char === '|') {
			cells.push(current.trim());
			current = '';
			continue;
		}
		current += char;
	}
	cells.push(current.trim());
	return cells;
}

function startsBlock(lines: readonly string[], index: number): boolean {
	const line = lines[index] ?? '';
	return (
		line.trim().length === 0 ||
		FENCE.test(line) ||
		HEADING.test(line) ||
		RULE.test(line) ||
		LIST_ITEM.test(line) ||
		line.trimStart().startsWith('>') ||
		(TABLE_SEPARATOR.test(lines[index + 1] ?? '') && line.includes('|'))
	);
}

/**
 * A SINGLE NEWLINE IS A SOFT BREAK, NOT A LAYOUT COMMAND.
 *
 * WHY THIS CHANGED. The model hard-wraps its prose at roughly one screen line,
 * and the renderer used to turn every one of those newlines into a <br>. The
 * words therefore arrived pre-broken at the model's own column, not at the
 * chat box's width: a line that was slightly too long for the box was clipped
 * at its right edge, which is the Owner's "cut in the middle" report. The
 * standard Markdown rule is the right one here — a single newline inside a
 * paragraph is a space, and two newlines are the paragraph break the block
 * parser already handles. An explicit hard break (two trailing spaces, or a
 * backslash) is preserved as a break, so a deliberate line break still works.
 */
function joinParagraphLines(lines: readonly string[]): string {
	let text = '';
	let previousHard = false;
	for (const line of lines) {
		const hard = / {2,}$/.test(line) || /\\$/.test(line);
		const clean = line.replace(/ {2,}$/, '').replace(/\\$/, '').trim();
		if (text.length === 0) text = clean;
		else text += `${previousHard ? '\n' : ' '}${clean}`;
		previousHard = hard;
	}
	return text;
}

/** The block language. The parser is line-oriented and bounded; every branch
 *  either consumes its construct or leaves the line to the paragraph branch. */
export function parseMarkdown(source: string): MarkdownBlock[] {
	const normalised = source.replace(/\r\n?/g, '\n');
	if (normalised.length > MARKDOWN_MAX_CHARS) {
		return [{ kind: 'paragraph', children: [{ kind: 'text', text: normalised }] }];
	}
	const lines = normalised.split('\n');
	const blocks: MarkdownBlock[] = [];
	let index = 0;

	while (index < lines.length) {
		const line = lines[index] as string;

		if (line.trim().length === 0) {
			index += 1;
			continue;
		}

		const fence = FENCE.exec(line);
		if (fence !== null) {
			const marker = fence[1] as string;
			const language = (fence[2] ?? '').length > 0 ? (fence[2] as string) : null;
			const body: string[] = [];
			index += 1;
			while (index < lines.length && !(lines[index] as string).trimStart().startsWith(marker)) {
				body.push(lines[index] as string);
				index += 1;
			}
			// AN UNCLOSED FENCE IS STILL A CODE BLOCK, to its end. That is the one
			// repair this parser makes, and it is the one a reader expects: the
			// alternative shows the fence markers and the code as prose.
			if (index < lines.length) index += 1;
			blocks.push({ kind: 'code', language, text: body.join('\n') });
			continue;
		}

		const heading = HEADING.exec(line);
		if (heading !== null) {
			const depth = (heading[1] as string).length;
			const level = (depth === 1 ? 2 : depth === 2 ? 3 : 4) as 2 | 3 | 4;
			blocks.push({
				kind: 'heading',
				level,
				children: parseMarkdownInline((heading[2] as string).trim()),
			});
			index += 1;
			continue;
		}

		if (RULE.test(line)) {
			blocks.push({ kind: 'rule' });
			index += 1;
			continue;
		}

		// A TABLE NEEDS ITS SEPARATOR; a line with pipes and no separator is a
		// paragraph, which is how prose that mentions `a | b` stays prose.
		if (
			line.includes('|') &&
			index + 1 < lines.length &&
			TABLE_SEPARATOR.test(lines[index + 1] as string)
		) {
			const header = splitTableRow(line).map((cell) => parseMarkdownInline(cell));
			const rows: MarkdownInline[][][] = [];
			index += 2;
			while (index < lines.length && (lines[index] as string).trim().length > 0) {
				const rowLine = lines[index] as string;
				if (!rowLine.includes('|')) break;
				const cells = splitTableRow(rowLine);
				// A SHORT ROW IS PADDED AND A LONG ONE IS KEPT. Dropping cells would
				// hide what was written; padding keeps the table rectangular.
				while (cells.length < header.length) cells.push('');
				rows.push(cells.slice(0, Math.max(header.length, cells.length)).map((cell) => parseMarkdownInline(cell)));
				index += 1;
			}
			blocks.push({ kind: 'table', header, rows });
			continue;
		}

		if (line.trimStart().startsWith('>')) {
			const quoted: string[] = [];
			while (index < lines.length && (lines[index] as string).trimStart().startsWith('>')) {
				quoted.push((lines[index] as string).replace(/^\s*>\s?/, ''));
				index += 1;
			}
			blocks.push({ kind: 'quote', blocks: parseMarkdown(quoted.join('\n')) });
			continue;
		}

		const listItem = LIST_ITEM.exec(line);
		if (listItem !== null) {
			const ordered = /^\d/.test(listItem[2] as string);
			const start = ordered ? Number.parseInt(listItem[2] as string, 10) : 1;
			const items: MarkdownInline[][] = [];
			while (index < lines.length) {
				const candidate = LIST_ITEM.exec(lines[index] as string);
				if (candidate === null || /^\d/.test(candidate[2] as string) !== ordered) break;
				items.push(parseMarkdownInline((candidate[3] as string).trim()));
				index += 1;
				// AN INDENTED CONTINUATION BELONGS TO THE ITEM.
				while (
					index < lines.length &&
					(lines[index] as string).trim().length > 0 &&
					/^\s{2,}/.test(lines[index] as string) &&
					!LIST_ITEM.test(lines[index] as string)
				) {
					const extra = (lines[index] as string).trim();
					const last = items[items.length - 1] as MarkdownInline[];
					last.push({ kind: 'break' }, { kind: 'text', text: extra });
					index += 1;
				}
			}
			blocks.push({ kind: 'list', ordered, start: Number.isFinite(start) ? start : 1, items });
			continue;
		}

		// A PARAGRAPH RUNS UNTIL A BLANK LINE OR THE START OF ANOTHER BLOCK.
		const paragraph: string[] = [line];
		index += 1;
		while (index < lines.length && !startsBlock(lines, index)) {
			paragraph.push(lines[index] as string);
			index += 1;
		}
		blocks.push({
			kind: 'paragraph',
			children: parseMarkdownInline(joinParagraphLines(paragraph)),
		});
	}

	return blocks;
}

function inlineText(nodes: readonly MarkdownInline[]): string {
	return nodes
		.map((node) => {
			switch (node.kind) {
				case 'text':
				case 'code':
					return node.text;
				case 'strong':
				case 'em':
					return inlineText(node.children);
				case 'link':
					return inlineText(node.children);
				case 'break':
					return '\n';
			}
		})
		.join('');
}

/**
 * THE SAME WORDS WITHOUT THE SYNTAX — used for a collapsed long turn, so the
 * preview never shows a literal `**` because the cut happened mid-marker. It
 * is a presentation transform only; the stored message is untouched.
 */
export function markdownToPlainText(source: string): string {
	const lines: string[] = [];
	for (const block of parseMarkdown(source)) {
		switch (block.kind) {
			case 'paragraph':
				lines.push(inlineText(block.children));
				break;
			case 'heading':
				lines.push(inlineText(block.children));
				break;
			case 'code':
				lines.push(block.text);
				break;
			case 'list':
				for (const [position, item] of block.items.entries()) {
					lines.push(`${block.ordered ? `${block.start + position}.` : '•'} ${inlineText(item)}`);
				}
				break;
			case 'quote':
				lines.push(inlineText([{ kind: 'text', text: block.blocks.map((entry) => {
					if (entry.kind === 'paragraph' || entry.kind === 'heading') return inlineText(entry.children);
					if (entry.kind === 'code') return entry.text;
					if (entry.kind === 'list') return entry.items.map((item) => inlineText(item)).join(' · ');
					if (entry.kind === 'quote') return '';
					return '';
				}).join('\n') }]));
				break;
			case 'rule':
				lines.push('');
				break;
			case 'table':
				for (const row of [block.header, ...block.rows]) {
					lines.push(row.map((cell) => inlineText(cell)).join(' · '));
				}
				break;
		}
	}
	return lines.join('\n');
}

function InlineNodes({ nodes }: { nodes: readonly MarkdownInline[] }): ComponentChildren {
	return (
		<>
			{nodes.map((node, index) => {
				switch (node.kind) {
					case 'text':
						return <span key={index}>{node.text}</span>;
					case 'code':
						return <code key={index}>{node.text}</code>;
					case 'strong':
						return (
							<strong key={index}>
								<InlineNodes nodes={node.children} />
							</strong>
						);
					case 'em':
						return (
							<em key={index}>
								<InlineNodes nodes={node.children} />
							</em>
						);
					case 'link':
						return (
							<a
								key={index}
								href={node.href}
								{...(node.external
									? { target: '_blank', rel: 'noopener noreferrer nofollow' }
									: {})}
							>
								<InlineNodes nodes={node.children} />
							</a>
						);
					case 'break':
						return <br key={index} />;
				}
			})}
		</>
	);
}

function Block({ block }: { block: MarkdownBlock }): ComponentChildren {
	switch (block.kind) {
		case 'paragraph':
			return (
				<p>
					<InlineNodes nodes={block.children} />
				</p>
			);
		case 'heading': {
			const children = <InlineNodes nodes={block.children} />;
			if (block.level === 2) return <h2>{children}</h2>;
			if (block.level === 3) return <h3>{children}</h3>;
			return <h4>{children}</h4>;
		}
		case 'code':
			return (
				<pre class="md-code" tabIndex={0} aria-label="code block">
					<code class={block.language === null ? undefined : `language-${block.language}`}>
						{block.text}
					</code>
				</pre>
			);
		case 'list': {
			const items = block.items.map((item, index) => (
				<li key={index}>
					<InlineNodes nodes={item} />
				</li>
			));
			return block.ordered ? (
				<ol start={block.start === 1 ? undefined : block.start}>{items}</ol>
			) : (
				<ul>{items}</ul>
			);
		}
		case 'quote':
			return (
				<blockquote>
					<MarkdownBlocks blocks={block.blocks} />
				</blockquote>
			);
		case 'rule':
			return <hr />;
		case 'table':
			return (
				<div class="md-table-wrap" tabIndex={0} role="region" aria-label="table">
					<table>
						<thead>
							<tr>
								{block.header.map((cell, index) => (
									<th key={index}>
										<InlineNodes nodes={cell} />
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{block.rows.map((row, rowIndex) => (
								<tr key={rowIndex}>
									{row.map((cell, cellIndex) => (
										<td key={cellIndex}>
											<InlineNodes nodes={cell} />
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			);
	}
}

function MarkdownBlocks({ blocks }: { blocks: readonly MarkdownBlock[] }): ComponentChildren {
	return (
		<>
			{blocks.map((block, index) => (
				<Block key={index} block={block} />
			))}
		</>
	);
}

/**
 * THE COMPONENT THE CHAT USES. A single `.markdown` wrapper, and nothing else:
 * a short conversational reply is one paragraph with no chrome, and a
 * technical answer gets the hierarchy its own syntax asked for.
 */
export function Markdown({
	content,
	class: className,
	transform,
}: {
	content: string;
	class?: string;
	/** A CALLER-OWNED POST-PARSE TRANSFORM. The chat uses it to turn known
	 *  record ids into internal links; the transform only ever sees the node
	 *  tree, so code and existing links remain untouched by construction. */
	transform?: (blocks: MarkdownBlock[]) => MarkdownBlock[];
}): ComponentChildren {
	const parsed = parseMarkdown(content);
	const blocks = transform === undefined ? parsed : transform(parsed);
	return (
		<div class={className === undefined ? 'markdown' : `markdown ${className}`}>
			<MarkdownBlocks blocks={blocks} />
		</div>
	);
}
